// Linked stage shapes: their geometry is a function of the version of their map they follow, written by the runner's derive and drawn from a held editor frame.
import { mapPath, outputToStage } from '~/lib/map'
import { heldMap, holdShapes } from '~/lib/map/held'
import { positionFromOrigin, worldToLocal } from '~/lib/objectBounds'
import { dropObjectWrites, holdObjectWrites } from '~/lib/objectWrites'
import { mapOutline } from '~/lib/outline/path'
import { getObjectInitial } from '~/lib/params'
import { resetPathOrigin } from '~/lib/svg/paths'
import { updateObject, updateObjects } from '~/store/live'
import { selectMaps, selectShapeMapData } from '~/store/maps/selectors'

const GEOMETRY = ['x', 'y', 'rotation', 'scaleX', 'scaleY']

/** The patch that puts stage-space outline `d` on shape `id`, in its parent's frame: geometry baked, rotation 0, scale 1. */
const geometryPatch = (objects, id, d) => {
	const obj = objects[id]
	const parent = objects[obj.parentId]
	const local =
		parent?.type === 'group'
			? mapOutline(d, ([x, y]) => {
					const p = worldToLocal({ x, y }, parent, objects)
					return [p.x, p.y]
				})
			: d
	if (!local) return null
	const { data, x, y } = resetPathOrigin(local)
	const pose = { data, rotation: 0, scaleX: 1, scaleY: 1 }
	return { id, ...pose, ...positionFromOrigin({ ...obj, ...pose }, { x, y }, objects) }
}

const sameGeometry = (obj, patch) =>
	obj.data === patch.data &&
	GEOMETRY.every(k => Math.abs((obj[k] ?? getObjectInitial(obj.type, k) ?? 0) - patch[k]) < 1e-6)

/** The patch for every linked shape whose geometry differs from its surface in `dataFor(mapId)` (a map's data, or undefined to skip it); `all` keeps the unchanged ones too. */
const linkedShapePatches = (objects, dataFor, all = false) => {
	const patches = []
	for (const [id, obj] of Object.entries(objects)) {
		if (!obj.map?.id) continue
		const data = dataFor(obj.map.id)
		const s = data?.surfaces[obj.map.surface]
		const d = s && mapPath(s.path, outputToStage(data, s))
		const patch = d && geometryPatch(objects, id, d)
		if (patch && (all || !sameGeometry(obj, patch))) patches.push(patch)
	}
	return patches
}

/** A detach for every linked shape whose saved map is readable and has no surface for it; a draft that drops a surface leaves the shape where it was until the save. */
const lostSurfacePatches = (objects, maps) => {
	const patches = []
	for (const [id, obj] of Object.entries(objects)) {
		const data = obj.map?.id && maps.find(m => m.id === obj.map.id)?.data
		if (data && !data.surfaces?.[obj.map.surface]) patches.push({ id, map: '' })
	}
	return patches
}

/** Every linked shape takes its geometry from the version of its map it follows (`selectShapeMapData`), or detaches once its saved map lost its surface, in one write the app's history never records. */
export const deriveLinkedShapes = () => (dispatch, getState) => {
	const s = getState()
	const { objects } = s.live
	const patches = [
		...lostSurfacePatches(objects, selectMaps(s)),
		...linkedShapePatches(objects, id => selectShapeMapData(s, id)),
	]
	if (patches.length) dispatch({ ...updateObjects(patches), meta: { mapDerived: true } })
}

/** The held editor frame drawn on the active map's linked shapes, outside Redux; with none held, drops what was drawn. The commit's derive writes them. */
export const holdLinkedShapes = () => (dispatch, getState) => {
	const held = heldMap()
	const s = getState()
	const id = s.maps.editing?.id
	if (!held || !id) {
		holdShapes([])
		if (!held) dropObjectWrites()
		return
	}
	const { objects } = s.live
	// Unchanged ones too: a frame that returns a shape to where it was must draw it back.
	const patches = linkedShapePatches(objects, mapId => (mapId === id ? held : undefined), true)
	holdShapes(patches.map(p => p.id))
	if (patches.length) holdObjectWrites(objects, write => patches.forEach(p => write(updateObject(p))))
}
